import { Component, HostListener, Input, OnInit } from "@angular/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import { TranslateService } from "@ngx-translate/core";
import { PlanningBlock } from "../../../../pb-screen/models/planning-block/planning-block.interface";
import { TransportOrder } from "../../../../pb-screen/models/transport-order/transport-order.interface";

export type ItemsType = 'planningBlocks' | 'transportOrders' | 'auto';

export interface PlanningactionsConfirmationModel {
  title: string;
  messageTranslationKey: string;
  confirmButtonTextTranslationKey?: string;
  cancelButtonTextTranslationKey?: string;
  executeIconClass?: string;
  showDontAskAgain?: boolean;
  rememberKey?: string;
  items?: PlanningBlock[] | TransportOrder[];
  itemsType?: ItemsType;
  maxVisibleRows?: number;
  showMoreToggle?: boolean;
}

export interface PlanningactionsConfirmationModalResult {
  confirmed: boolean;
  dontAskAgain: boolean;
}

@Component({
  selector: 'planningactions-confirmation-modal',
  templateUrl: './planningactions-confirmation-modal.component.html',
  styleUrls: ['./planningactions-confirmation-modal.component.scss']
})
export class PlanningactionsConfirmationModalComponent
  extends SimpleModalComponent<PlanningactionsConfirmationModel, PlanningactionsConfirmationModalResult>
  implements OnInit, PlanningactionsConfirmationModel {

  @Input() title: string;
  @Input() messageTranslationKey: string;
  @Input() confirmButtonTextTranslationKey = 'CONFIRMATION_MODAL.OPTION_YES';
  @Input() showDontAskAgain = true;
  @Input() rememberKey?: string | undefined;
  @Input() items?: PlanningBlock[] | TransportOrder[];
  @Input() itemsType: ItemsType = 'auto';

  @Input() draggable = true;

  // view state
  dontAskAgain = false;
  internalItemsType: 'planningBlocks' | 'transportOrders' | undefined;
  expanded = false;

  constructor(private readonly translate: TranslateService) {
    super();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.cancel();
    }
    if (event.key === 'Enter' || event.key === 'Return') {
      this.confirm();
    }
  }

  ngOnInit(): void {
    // Determine items type if needed
    this.internalItemsType = this.resolveItemsType();

    // Bypass if already remembered
    try {
      if (this.rememberKey) {
        const stored = localStorage.getItem(this.storageKey(this.rememberKey));
        if (stored === 'true') {
          this.result = { confirmed: true, dontAskAgain: true };
          // close immediately without rendering
          this.close().then();
          return;
        }
      }
    } catch {
      // ignore storage errors
    }
  }

  private storageKey(key: string): string { return `pacm:${key}`; }

  private resolveItemsType(): 'planningBlocks' | 'transportOrders' | undefined {
    if (!this.items || (Array.isArray(this.items) && this.items.length === 0)) return undefined;
    if (this.itemsType && this.itemsType !== 'auto') return this.itemsType;

    const first: any = (this.items as any[])[0];
    // Heuristic: PlanningBlock often has id, orderLineId, statusType; TransportOrder has orderId/transportOrderId/reference
    if (first && (first.statusType !== undefined || first.orderLineId !== undefined)) {
      return 'planningBlocks';
    }
    return 'transportOrders';
  }

  confirm(): void {
    if (this.dontAskAgain && this.rememberKey) {
      try {
        localStorage.setItem(this.storageKey(this.rememberKey), 'true');
      } catch {
        // ignore
      }
    }
    this.result = { confirmed: true, dontAskAgain: this.dontAskAgain };
    this.close().then();
  }

  cancel(): void {
    this.result = { confirmed: false, dontAskAgain: false };
    this.close().then();
  }
}
